import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MetricsService } from '@jeppesen-foreflight/jmkw-analytics';
import { PurlyService } from '@jeppesen-foreflight/jmkw-core';
import { ToolkitApiService } from '@jeppesen-foreflight/jmkw-toolkit';
import {
  JmkwEvent,
  Plugin,
  PluginService
} from '@jeppesen-foreflight/jmkw-types';
import { NGXLogger } from 'ngx-logger';
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  filter,
  map,
  skip,
  take
} from 'rxjs';
import { PluginIDs } from '../../../../plugin-ids.type';
import { DEFAULT_PERSONA, Persona } from '../../../types/persona.model';
import { AVIATION_CONFIG_TOKEN } from '../../aviation.config';
import { getPurlyUrlForLayer } from '../../utilities/purly-url-mapping.util';
import { bindPurlyVectorLayer, PurlyLayerBinding } from '../../utilities/purly-binding.util';
import { NavigationPopoutComponent } from './buttons/navigation-button/navigation-button-popout/navigation-popout.component';
import { NavigationButtonComponent } from './buttons/navigation-button/navigation-button.component';
import { IFR_TOGGLE_PLUGIN } from './ifr.plugin';
import { NavigationLayer } from './layer/navigation.layer';
import { NavigationLayerReadyEvent } from './navigation.events';
import { PERSONA_TOGGLE_PLUGIN } from './persona.plugin';
import { SelectedItemService } from './selected-item/selected-item.service';
import { ManualFeatureSelectionProperties } from './selected-item/typings/manual-feature-selection-properties';

/**
 * Navigation Plugin Service
 *
 * This service manages the Navigation layer which is uniquely complex among aviation plugins
 * due to its persona-based URL structure. Unlike other plugins that have a single URL string
 * in the effectivity solution, navigation has a Record<Persona, string> containing different
 * URLs for different user roles (InFlight vs Operations).
 *
 * Key Complexities:
 * 1. The backend returns navigation as an object, not a string, requiring transformation
 * 2. Layer must be replaced when either effectivity OR persona changes
 * 3. State (IFR mode, selection) must be preserved across replacements
 * 4. Persona state must bridge across layer instances during AIRAC updates
 *
 * @implements {PluginService}
 */
@Injectable({ providedIn: 'root' })
export class NavigationPluginService implements PluginService {
  private readonly selectedItemSvc = inject(SelectedItemService);
  private readonly api = inject(ToolkitApiService);
  private readonly _config = inject(AVIATION_CONFIG_TOKEN);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly purlyService = inject(PurlyService);
  protected readonly metrics = inject(MetricsService);
  protected readonly logger = inject(NGXLogger);

  readonly id: PluginIDs = 'navigation';

  private _layerBinding: PurlyLayerBinding<NavigationLayer> | undefined;
  private _personaSubject!: BehaviorSubject<Persona>;

  private get layer(): NavigationLayer | undefined {
    return this._layerBinding?.layer;
  }
  /**
   * Creates the Navigation plugin with Purly-based layer management.
   *
   * Unlike other aviation plugins, Navigation requires special handling because:
   * - The Purly service contains URLs for multiple personas (user roles)
   * - The active URL depends on both the signed URLs AND the selected persona
   * - The layer must reload when either changes
   *
   * @returns Observable<Plugin> that emits once Purly URLs are available
   */
  create(): Observable<Plugin> {
    return this.purlyService.initializationResult$.pipe(
      take(1),
      map((result) => {
        const persona = this.getPersona();

        // Initialize persona bridge for tracking across layer replacements
        this._personaSubject = new BehaviorSubject<Persona>(persona);

        // Setup binding utility with persona-aware URL handling
        this._layerBinding = bindPurlyVectorLayer({
          // Combined observable: Purly updates + persona changes
          // Note: We skip(1) the combined result to avoid processing the initial emission
          // (since initialResult handles the initial layer creation), but this allows
          // the stream to emit when EITHER purly or persona changes
          purly$: combineLatest([
            this.purlyService.initializationResult$,
            this._personaSubject.asObservable()
          ]).pipe(
            skip(1), // Skip the initial combined emission
            map(([result]) => result), // Pass through result, but trigger on persona change
            filter(r => r?.success === true)
          ),
          toolkitApi: this.api,
          makeLayer: (url: string) => {
            const layer = new NavigationLayer(
              {
                id: this.id,
                url,
                zOrder: 80,
                label: 'Navigation'
              },
              this._personaSubject.value,
              this.logger
            );

            // Subscribe to layer's persona changes and update bridge
            layer.persona$.subscribe(p => this._personaSubject.next(p));

            return layer;
          },
          extractUrl: (result) => {
            return getPurlyUrlForLayer(
              'navigation',
              this.purlyService.getTileServiceUrls(),
              this._personaSubject.value
            );
          },
          // Custom replacement to preserve IFR state and re-initialize
          createReplacement: async (currentLayer, url) => {
            const newLayer = new NavigationLayer(
              {
                id: this.id,
                url,
                zOrder: 80,
                label: 'Navigation'
              },
              this._personaSubject.value,
              this.logger
            );

            // Transfer IFR state from old layer to new
            if (currentLayer?.ifr !== undefined) {
              newLayer.ifr = currentLayer.ifr;
            }

            // Subscribe to new layer's persona changes
            newLayer.persona$.subscribe(p => this._personaSubject.next(p));

            // Replace in map
            await this.api.replaceLayer(newLayer);

            // Re-initialize styles and feature selection
            if (newLayer.loaded) {
              newLayer.initializeIFRVectorStyles();
            }
            this.setupFeatureSelectionHandlers();

            return newLayer;
          },
          pluginIds: { parent: 'aviation', child: this.id },
          logger: this.logger,
          initialResult: result
        });

        const layer = this._layerBinding.layer;

        if (!layer) {
          this.logger.warn(
            'Navigation plugin: No layer created from binding utility'
          );
        }

        // Initialize layer and feature selection
        void this.initializeLayer();

        return new Plugin({
          id: this.id,
          layer: layer as NavigationLayer,
          button: {
            id: 'navigation',
            iconName: 'icon-aviation',
            automationKey: 'navigation-button',
            tooltipText: 'Navigation Options',
            buttonComponent: NavigationButtonComponent,
            contentComponent: NavigationPopoutComponent
          },
          children: [IFR_TOGGLE_PLUGIN, PERSONA_TOGGLE_PLUGIN]
        });
      })
    );
  }

  private async initializeLayer(): Promise<void> {
    const layer = this.layer;
    if (!layer) {
      this.logger.error('Failed to initialize: No layer available');
      return;
    }

    try {
      await layer.load();
      this.initializeLayerStyles();

      // Wire up layer ready handler
      this.api
        .onLayerReady$<NavigationLayer>(this.id)
        .pipe(take(1), takeUntilDestroyed(this._destroyRef))
        .subscribe(() => this.setupFeatureSelectionHandlers());

      this.api.dispatchEvent(new NavigationLayerReadyEvent());
    } catch (error) {
      this.logger.error('Failed to initialize navigation layer:', error);
    }
  }

  private initializeLayerStyles(): void {
    const layer = this.layer;
    if (!layer?.loaded) return;
    layer.initializeIFRVectorStyles();
  }

  private setupFeatureSelectionHandlers(): void {
    const layer = this.layer;
    if (!layer) return;

    this.setupManualSelectionListener();
    if (this._config.useDefaultClickHandler !== false) {
      this.selectedItemSvc.updateClickHandler(layer);
      this.selectedItemSvc.clearExistingSelection();
    }
  }

  private setupManualSelectionListener(): void {
    this.api
      .getEventStream()
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter(
          (evt: JmkwEvent) =>
            (evt?.event as { type?: string })?.type ===
            'manual-feature-selection'
        )
      )
      .subscribe((evt: JmkwEvent) => {
        const props = evt.source as
          | ManualFeatureSelectionProperties
          | undefined;
        if (props) this.selectedItemSvc.selectFeature(props);
      });
  }

  private getPersona(): Persona {
    return this._config.persona || DEFAULT_PERSONA;
  }
}
